import { styled } from '@mui/material';
import NoRubrics from 'components/Rubrics/NoRubrics';
import { getRubricTemplateTotalPoints } from 'components/Rubrics/Table';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Button, Container, Divider, Icon, IconButton, Link, Paper, Stack, Text, TextInput } from 'shell/components';
import { roundTo } from 'utils/helper';

const Mobile = ({
    rubrics,
    filters,
    setFilters,
    getFilterCount,
    onClickEdit,
    onClickDownloadCSV,
    onClickDelete,
    sortState,
}) => {
    const { t } = useTranslation('assignment');
    const history = useHistory();

    return (
        <Container device={'mobile'}>
            <CustomStack spacing={5}>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text component={'h1'} variant={'h6'} weight={'semibold'}>
                        {t('rubric.page.title', 'Rubric Templates')}
                    </Text>
                    <Button startIcon={<Icon kind={'add'} />} onClick={() => history.push('/rubrics/get-started')}>
                        {t('rubric.page.new-rubric', 'New Rubric')}
                    </Button>
                </Stack>
                <Stack alignItems={'center'} direction={'row'} spacing={4}>
                    <CustomButton
                        color={'neutral'}
                        startIcon={<Icon kind={'filter'} />}
                        variant={'secondary'}
                        onClick={() => setFilters((current) => ({ ...current, open: true }))}
                    >
                        ({getFilterCount(filters, sortState)})
                    </CustomButton>
                    <TextInput
                        aria-label={'search'}
                        endIcon={<Icon kind={'search'} />}
                        placeholder={t('common:controls.search', 'Search')}
                        value={filters.search}
                        onChange={(e) => setFilters((current) => ({ ...current, search: e.target.value }))}
                    />
                </Stack>
                {rubrics.length ? (
                    <Stack spacing={4}>
                        {rubrics.map((rubric) => (
                            <Paper key={rubric.id} variant={'outlined'}>
                                <Stack divider={<Divider />} spacing={3}>
                                    <Stack spacing={2}>
                                        <Stack direction={'row-reverse'} spacing={4}>
                                            <IconButton
                                                aria-label={'delete rubric'}
                                                kind={'delete'}
                                                size={'medium'}
                                                tooltip={{ component: t('common:controls.delete', 'Delete') }}
                                                variant={'square'}
                                                onClick={() => onClickDelete(rubric)}
                                            />
                                            <IconButton
                                                aria-label={'download rubric'}
                                                kind={'download'}
                                                size={'medium'}
                                                tooltip={{
                                                    component: t('common:controls.download-csv', 'Download CSV'),
                                                }}
                                                variant={'square'}
                                                onClick={() => onClickDownloadCSV(rubric)}
                                            />
                                            <IconButton
                                                aria-label={'edit rubric'}
                                                kind={'edit'}
                                                size={'medium'}
                                                tooltip={{ component: t('common:controls.edit', 'Edit') }}
                                                variant={'square'}
                                                onClick={() => onClickEdit(rubric)}
                                            />
                                        </Stack>
                                        <Link to={`/rubrics/${rubric.id}`}>{rubric.rubric.title}</Link>
                                    </Stack>
                                    <Stack spacing={2}>
                                        <Text color={'light'}>
                                            {t('common:table.last-updated', 'Last Updated')}:{' '}
                                            {t('common:ui.format-date-time', { val: rubric.modified_on })}
                                        </Text>
                                        <Text color={'light'}>
                                            {t('common:table.criteria', 'Criteria')}: {rubric.rubric.criteria.length}
                                        </Text>
                                        <Text color={'light'}>
                                            {t('common:table.points', 'Points')}:{' '}
                                            {getRubricTemplateTotalPoints(rubric.rubric) === null
                                                ? t('common:status.unscored', 'Unscored')
                                                : roundTo(getRubricTemplateTotalPoints(rubric.rubric))}
                                        </Text>
                                    </Stack>
                                </Stack>
                            </Paper>
                        ))}
                    </Stack>
                ) : (
                    <NoRubrics />
                )}
            </CustomStack>
        </Container>
    );
};

const CustomStack = styled(Stack)(({ theme }) => {
    return {
        width: '100%',
        paddingBottom: theme.spacing(5),
    };
});

const CustomButton = styled(Button)(() => {
    return {
        flexShrink: 0,
    };
});

export default Mobile;
